/**
 * Acceso rápido «Venta rápida» del inicio.
 *
 * El dueño pidió el flujo en los dos lados: como solapa en la ficha del cliente
 * y como acceso rápido desde el inicio. Este es el segundo, y **no reimplementa
 * nada**: pregunta a qué cliente se le vende y abre exactamente el mismo
 * `VentaRapidaDialog` que la ficha. Dos selectores distintos habrían sido dos
 * precios distintos el día que uno de los dos se quedara viejo.
 *
 * El buscador de cliente es el `RelationPicker` genérico de la app —el mismo que
 * usa cualquier many2one—, así que respeta los permisos y las reglas de fila del
 * usuario sin una línea de filtro de este lado.
 *
 * Se oculta solo si el usuario no puede vender: la sección entera desaparece en
 * vez de mostrar un botón que va a contestar 403. Es el mismo criterio que la
 * ventana «Próximos a vencer».
 *
 * Dos huecos distintos, y por eso dos conductas distintas
 * -------------------------------------------------------
 * * **Al usuario no le toca vender** (no tiene los grupos): la sección no se
 *   dibuja, muda. El párrafo de abajo explica por qué.
 * * **Al usuario le toca, pero está parado en una empresa que no vende** (un
 *   consorcio, que liquida expensas): la sección **sí** se dibuja y cuenta qué
 *   pasa, porque acá el hueco es temporal y la persona lo puede levantar en un
 *   clic. Ésta es la puerta por la que entra el vendedor nuevo el primer día:
 *   antes anotaba la venta sin que nadie le dijera nada y el sistema se la
 *   rechazaba días después hablándole de un impuesto que nunca tocó. Ver
 *   `views/EmpresaQueVende`.
 *
 * **Éste es el único lugar del relevamiento que se deja mudo a propósito, y
 * conviene que quede escrito por qué.** La regla del sistema es que nada se
 * apaga en silencio (ver `views/MotivoBloqueo`), y en la ficha de un contacto sí
 * se dice —«tu usuario no tiene permiso para…»—, porque ahí la persona fue a
 * buscar algo concreto y se encontró con un hueco. Acá no: esto es la pantalla
 * de inicio, la ve todo el mundo todos los días, y escribirle a los roles que
 * nunca van a vender que existe una función que no pueden usar es ruido
 * permanente para el 100 % a cambio de información para casi nadie. Además, la
 * acción ni siquiera llega al cliente: el backend la poda por grupos
 * (`api.py::_actions_metadata`), justamente para no contarle a cada usuario cómo
 * está organizada la empresa.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { RelationPicker } from '@/components/fields/RelationPicker';
import { IconShoppingCart } from '@/components/shell/icons';
import { Button, Card } from '@/components/shell/primitives';
import type { Many2OneValue } from '@/lib/types';
import { useEstadoDeVentaRapida, useSalidaAEmpresaQueVende } from '@/views/EmpresaQueVende';
import { AvisoDeBloqueo } from '@/views/MotivoBloqueo';
import { useInvalidateData } from '@/views/queries';
import { VentaRapidaDialog } from '@/views/VentaRapidaDialog';

export function VentaRapidaLauncher() {
  const [eligiendo, setEligiendo] = useState(false);
  const [cliente, setCliente] = useState<Many2OneValue | null>(null);
  const queryClient = useQueryClient();
  const invalidar = useInvalidateData();

  /**
   * ¿Este usuario puede vender, y desde la empresa que tiene activa?
   *
   * Son dos preguntas y llegan en la misma respuesta del servidor. Que la acción
   * **exista** es el permiso: el backend poda por grupos las que no le tocan al
   * usuario. Que esté **habilitada** es la empresa del selector: el backend la
   * apaga —con su motivo escrito— cuando la empresa activa liquida expensas y no
   * vende. Ver `views/EmpresaQueVende`.
   */
  const venta = useEstadoDeVentaRapida();
  const salida = useSalidaAEmpresaQueVende();

  if (venta.cargando || !venta.existe) return null;

  return (
    <section aria-labelledby="venta-rapida-titulo-inicio" className="mt-6">
      <h2
        id="venta-rapida-titulo-inicio"
        className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-fg-faint"
      >
        <IconShoppingCart className="h-4 w-4 text-accent" />
        Venta rápida
      </h2>

      <Card className="p-4">
        {/* Parado en una empresa que no vende, el acceso rápido NO ofrece el
            botón: dice dónde está la persona, qué va a pasar si insiste y ofrece
            el cambio de empresa a un clic. Es el cartel en el lugar donde fue a
            buscar la función, no arriba de todo con los demás. El texto sale del
            servidor; el botón sólo cambia de empresa y no vende nada. */}
        {!venta.habilitada ? (
          <AvisoDeBloqueo tono="atencion" motivo={venta.motivo} salida={salida} />
        ) : !eligiendo ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-fg-muted">
              Elegí un cliente, marcá los productos que le vendiste y queda anotado con su
              vencimiento. No emite ninguna factura.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEligiendo(true);
              }}
            >
              Vender a un cliente
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[16rem] flex-1">
              <label htmlFor="venta-rapida-cliente" className="mb-1 block text-sm font-medium">
                ¿A quién le vendemos?
              </label>
              <RelationPicker
                id="venta-rapida-cliente"
                relation="base.partner"
                placeholder="Buscar un cliente…"
                autoFocus
                onPick={(registro) => {
                  setCliente(registro);
                }}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEligiendo(false);
                setCliente(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        )}
      </Card>

      <VentaRapidaDialog
        partnerId={cliente?.id ?? null}
        partnerName={cliente?.display_name ?? ''}
        onClose={() => {
          setCliente(null);
          setEligiendo(false);
        }}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['venta-rapida-totales'] });
          // Lo vendido alimenta la agenda «Próximos a vencer» de esta misma
          // pantalla: si el plan que se acaba de vender vence pronto, tiene que
          // aparecer sin recargar.
          void queryClient.invalidateQueries({ queryKey: ['proximos-vencer'] });
          void invalidar('sales.subscription');
        }}
      />
    </section>
  );
}
